export const MAX_HOURS_THRESHOLD = 24;
export const HOURS_STEP = 0.5;

export interface OfficeDayRulesValues {
  minimumOfficeDaysPerMonth: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
}

export interface OfficeDayRulesFieldErrors {
  minimumOfficeDaysPerMonth?: string;
  fullDayHoursThreshold?: string;
  shortDayHoursThreshold?: string;
}

export const formatHoursThreshold = (hours: number): string => {
  if (!Number.isFinite(hours)) return '0';
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
};

export const buildPresentStatusLabels = (
  fullDayHoursThreshold: number,
  shortDayHoursThreshold: number
) => {
  const full = formatHoursThreshold(fullDayHoursThreshold);
  const short = formatHoursThreshold(shortDayHoursThreshold);

  return {
    present: `Present (${full}h+)`,
    shortDay: `Short Day (${short}–${full}h)`,
    veryShort: `Too Short (<${short}h)`,
    missing: 'Missing',
    fullDayLeave: 'Full Day Leave',
    halfDayLeave: 'Half Day Leave'
  };
};

export const validateOfficeDayRules = (
  values: OfficeDayRulesValues
): OfficeDayRulesFieldErrors => {
  const errors: OfficeDayRulesFieldErrors = {};

  if (
    !Number.isInteger(values.minimumOfficeDaysPerMonth) ||
    values.minimumOfficeDaysPerMonth < 1 ||
    values.minimumOfficeDaysPerMonth > 31
  ) {
    errors.minimumOfficeDaysPerMonth = 'Must be a whole number between 1 and 31.';
  }

  if (!isValidHoursThreshold(values.shortDayHoursThreshold)) {
    errors.shortDayHoursThreshold =
      `Must be greater than 0, at most ${MAX_HOURS_THRESHOLD}, in ${HOURS_STEP}h steps.`;
  }

  if (!isValidHoursThreshold(values.fullDayHoursThreshold)) {
    errors.fullDayHoursThreshold =
      `Must be greater than 0, at most ${MAX_HOURS_THRESHOLD}, in ${HOURS_STEP}h steps.`;
  } else if (
    isValidHoursThreshold(values.shortDayHoursThreshold) &&
    values.fullDayHoursThreshold <= values.shortDayHoursThreshold
  ) {
    errors.fullDayHoursThreshold =
      'Must be greater than the short day threshold.';
  }

  return errors;
};

const isValidHoursThreshold = (value: number): boolean => {
  if (!Number.isFinite(value) || value <= 0 || value > MAX_HOURS_THRESHOLD) {
    return false;
  }

  return Math.abs((value * 2) % 1) < 0.0001;
};
